/**
 * Shared API Client for Frontend Applications
 * Routes all requests through the backend API (no direct Supabase access)
 */

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => string | null;
  getSessionKey?: () => string | null;
  /** Extra headers on every request (e.g. x-plutos-client for Android TWA). */
  getClientHeaders?: () => Record<string, string>;
}

export interface ApiRequestOptions {
  headers?: Record<string, string>;
  includeAuth?: boolean;
  includeSessionKey?: boolean;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  meta?: {
    timestamp: string;
    requestId?: string;
  };
}

/**
 * Create a configured API client
 */
export function createApiClient(config: ApiClientConfig) {
  const { baseUrl, getToken, getSessionKey, getClientHeaders } = config;

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const url = `${baseUrl}${path}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getClientHeaders?.(),
      ...options.headers,
    };

    // Add auth token if available
    const token = getToken?.();
    if (options.includeAuth !== false && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const sessionKey = getSessionKey?.();
    if (options.includeSessionKey !== false && sessionKey) {
      headers['X-Plutos-Session-Id'] = sessionKey;
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include', // Include cookies for session management
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || { 
            code: `HTTP_${response.status}`, 
            message: data.message || 'Request failed' 
          },
        };
      }

      // Handle both wrapped and unwrapped responses
      if ('success' in data) {
        return data;
      }
      
      return {
        success: true,
        data: data as T,
      };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Network error';
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message,
        },
      };
    }
  }

  return {
    get: <T>(path: string, options?: ApiRequestOptions) => 
      request<T>('GET', path, undefined, options),
    
    post: <T>(path: string, body?: unknown, options?: ApiRequestOptions) => 
      request<T>('POST', path, body, options),
    
    put: <T>(path: string, body?: unknown, options?: ApiRequestOptions) => 
      request<T>('PUT', path, body, options),
    
    patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions) => 
      request<T>('PATCH', path, body, options),
    
    delete: <T>(path: string, options?: ApiRequestOptions) => 
      request<T>('DELETE', path, undefined, options),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
